import React, { Fragment, useEffect } from 'react';
import {
  MenuItem,
  Select,
  TextField,
  Typography,
  Switch,
  FormGroup,
  FormControlLabel,
  FormControl,
  InputLabel,
} from '@mui/material';
import { AlertCircle } from 'react-feather';
import FilterAccordian from '../filterAccordian';
import Breadcrumb from '../common/breadcrumb';
import { userRequest } from '../../auth/requestMethods';
import MenuBook from '@mui/icons-material/MenuBook';
import { Modal, ModalHeader, ModalBody, Alert } from 'reactstrap';
import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { toast } from 'react-toastify';
import CustomTable from '../tables/MuiDataGrid';

const Manage = ({ history }) => {
  useEffect(() => {
    document.title = 'PFSA | Menus';
  }, []);
  const [modalState, setModalState] = React.useState({
    update: false,
    open: false,
  });
  const [perPage, setPerPage] = React.useState(10);
  const [page, setPage] = React.useState(1);
  const [dataIsFetching, setDataIsFetching] = React.useState(true);

  const [menuNames, setMenuNames] = React.useState({
    count: 0,
    menus: [],
  });
  const [schoolsNames, setSchoolNames] = React.useState({
    count: 0,
    schools: [],
  });

  const [skuNames, setSkuNames] = React.useState({
    count: 0,
    skus: [],
  });

  const [tableData, setTableData] = React.useState({
    count: 0,
    menus: [],
  });
  const [searchInput, setSearchInput] = React.useState({
    schools: 'All',
    total_count: 'All',
    is_active: 'All',
    display_name: 'All',
    sku: 'All',
  });
  const [filterOptions, setFilterOptions] = React.useState({
    skuTypes: [],
    skus: [],
  });

  const {
    register,
    handleSubmit,
    watch,
    reset,
    trigger,
    getValues,
    // clearErrors,
    formState: { errors, isSubmitted },
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues,
  });

  React.useEffect(() => {
    trigger();
    const subscription = watch((value, { name }) => trigger(name));
    return () => subscription.unsubscribe();
  }, [watch, trigger]);

  React.useEffect(() => {
    bulkAxios(['/sku-types/namesUsed', '/skus/names'])
      .then(async (all) => {
        setFilterOptions((pr) => ({
          skuTypes: all[0].skuTypes,
          skus: all[1].skus,
        }));
      })
      .catch((err) => console.log(err));
  }, []);

  React.useEffect(() => {
    const fetchItems = async () => {
      try {
        setDataIsFetching(true);

        userRequest()
          .get(`/menus?per_page=${perPage}&page=${page}&${buildQuery()}`)
          .then(({ data }) => {
            setTableData({
              count: data.total,
              menus: data.menus,
            });
            setDataIsFetching(false);
          })
          .catch((error) => {
            setDataIsFetching(false);
            console.error('Error fetching items:', error);
          });
      } catch (error) {
        setDataIsFetching(false);
        console.error('Error fetching items:', error);
      }
    };

    fetchItems();
  }, [perPage, page, searchInput]);

  function getFilters() {
    return Object.entries(searchInput)
      .map(([key, value]) => {
        if (value !== '') {
          if (key === 'schools') {
            if (value === 'All') {
              return [];
            } else {
              return `School:${value}`;
            }
          }
          if (key === 'sku') {
            if (value === 'All') {
              return [];
            } else {
              const skuType = filterOptions.skuTypes.find((x) => x.id === value);
              return skuType ? `SKU Type: ${skuType.display_name}` : null;
            }
          }
          if (key === 'display_name') {
            if (value === 'All') {
              return [];
            } else {
              return `Menu: ${value}`;
            }
          }
          if (key === 'is_active') {
            if (value === 'All') {
              return [];
            } else {
              return `Status: ${value === 'ACTIVE' ? 'Active' : 'Inactive'}`;
            }
          }
        }
      })
      .filter((x) => x !== undefined && x.length !== 0);
  }

  function bulkAxios(urls = null) {
    if (!urls) return urls; // Handle empty URLs array

    let promises = urls.map((req) =>
      userRequest()
        .get(req)
        .then(({ data }) => {
          return data;
        })
        .catch((error) => {
          console.log(error);
          return null;
        })
    );

    return Promise.all(promises);
  }

  function buildQuery(foundFilter = null) {
    return Object.entries(searchInput)
      .map(([key, value]) => {
        if (value !== '') {
          if (foundFilter) foundFilter.push(key);
          if (key === 'is_active') {
            if (value === 'ALL') {
              key = '';
              value = '';
            } else if (value === 'ACTIVE') {
              value = 'true';
            } else if (value === 'INACTIVE') {
              value = 'false';
            }
          }

          if (key === 'sku') {
            const skuType = filterOptions.skuTypes.find((x) => x.id == value);
            value = skuType ? skuType.display_name : undefined;
            if (!value) return '';
          }

          if (value === 'All') {
            return '';
          }

          return encodeURIComponent(key) + '=' + encodeURIComponent(value);
        }
        return '';
      })
      .filter((Q) => !!Q)
      .join('&');
  }
  React.useEffect(() => {
    userRequest()
      .get(`/menus/names`)
      .then(({ data }) => {
        setMenuNames({
          count: data.total,
          menus: data.menus,
        });
      })
      .catch(({ response }) => console.error(response));
  }, []);

  React.useEffect(() => {
    userRequest()
      .get('/schools/names')
      .then(({ data }) => {
        setSchoolNames({
          schools: data.schools,
          count: data.schools.length,
        });
      })
      .catch(({ response }) => {
        console.error(response);
      });
  }, []);

  React.useEffect(() => {
    userRequest()
      .get('/sku-types/names')
      .then(({ data }) => {
        setSkuNames({
          skus: data.skuTypes,
          count: data.skuTypes.length,
        });
      })
      .catch(({ response }) => {
        console.error(response);
      });
  }, []);

  function setStatus(id, data) {
    userRequest()
      .post(`/menus/update/${id}`, data)
      .then(() => {
        let menus = [];
        if (meetFilterConditions(data)) {
          menus = tableData.menus.map((menu) => {
            if (id === menu.id) menu.is_active = !menu.is_active;
            return menu;
          });
        } else {
          menus = tableData.menus.filter((menu) => id !== menu.id);
        }
        setTableData({
          count: menus.length,
          menus,
        });
      })
      .catch(() => {});
  }
  function meetFilterConditions(data, isUpdate = true) {
    if (!isUpdate) {
      if (tableData.count === perPage) return false;
    }
    return true;
  }
  const actionMethods = {
    toggleEdit: (row) => {
      setModalState({ open: true, update: row.id });
      reset(
        Object.keys(defaultValues).reduce((reduced, curr) => {
          reduced[curr] = row[curr];
          return reduced;
        }, {})
      );
      // clearErrors();
    },
    toggleActive: (row) => setStatus(row.id, { is_active: !row.is_active }),
  };
  const columns = [
    {
      field: 'display_name',
      headerName: 'Menu',
      flex: 1,
    },
    {
      headerAlign: 'left',
      align: 'left',
      field: 'schools',
      headerName: 'Schools',
      type: 'number',
      flex: 1,
      valueGetter: ({ row }) => (row.schools ? row.schools : 0),
    },
    {
      headerAlign: 'left',
      align: 'left',
      field: 'mo',
      headerName: 'Mo',
      type: 'number',
      flex: 1,
      valueGetter: ({ row }) => row.mo || 0,
    },
    {
      headerAlign: 'left',
      align: 'left',
      field: 'tu',
      headerName: 'Tu',
      type: 'number',
      flex: 1,
      valueGetter: ({ row }) => row.tu || 0,
    },
    {
      headerAlign: 'left',
      align: 'left',
      field: 'we',
      headerName: 'We',
      type: 'number',
      flex: 1,
      valueGetter: ({ row }) => row.we || 0,
    },
    {
      headerAlign: 'left',
      align: 'left',
      field: 'th',
      headerName: 'Th',
      type: 'number',
      flex: 1,
      valueGetter: ({ row }) => row.th || 0,
    },
    {
      headerAlign: 'left',
      align: 'left',
      field: 'fr',
      headerName: 'Fr',
      type: 'number',
      flex: 1,
      valueGetter: ({ row }) => row.fr || 0,
    },
    {
      field: 'is_active',
      headerName: 'Status',
      headerAlign: 'center',
      width: 110,
      renderCell: ({ row }) =>
        row.is_active ? (
          <Typography className="font-success mx-auto">Active</Typography>
        ) : (
          <Typography className="font-danger mx-auto">Inactive</Typography>
        ),
    },
  ];

  const onSubmit = (data) => {
    if (data !== '') {
      toast.info(`${modalState.update ? 'Updating...' : 'Creating...'}`, {
        isLoading: true,
        position: 'bottom-center',
        autoClose: 5000,
        theme: 'colored',
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        toastId: 'create',
      });
      let api = modalState.update ? `/menus/update/${modalState.update}` : '/menus';

      let { is_active, display_name } = data;

      const display_value = display_name;

      userRequest()
        .post(api, { is_active, display_name: display_value })
        .then((res) => {
          toast.dismiss('create');
          toast.success(`Successfully ${modalState.update ? 'updated' : 'created'}`, {
            position: 'bottom-center',
            autoClose: 5000,
            closeOnClick: true,
            draggable: true,
          });
          const menu = res.data.menu;
          let menus = [];
          if (modalState.update) {
            menus = tableData.menus.reduce((pre, curr) => {
              if (menu.id === curr.id) {
                meetFilterConditions({ ...curr, ...menu }, true) && pre.push({ ...curr, ...menu });
              } else pre.push(curr);

              return pre;
            }, []);
          } else {
            menus = meetFilterConditions(menu, false) ? [...tableData.menus, menu] : tableData.menus;
          }
          setTableData({ count: menus.length, menus });
          setModalState({ update: true, open: false });
        })
        .catch(({ response }) => {
          toast.dismiss('create');
          toast.error(response.data.message, {
            position: 'bottom-center',
            autoClose: 5000,
            closeOnClick: true,
            draggable: true,
          });
        });
    }
  };

  const onChangeInput = (e, val, name = null) => {
    if (name) setSearchInput({ ...searchInput, [name]: val });
    else setSearchInput({ ...searchInput, [e.target.name]: e.target.value });
  };

  const onSearch = (e) => {
    e.preventDefault();

    const queryString = buildQuery();
    console.log('Query String:', queryString);
    userRequest()
      .get(`/menus?per_page=${perPage}&page=${page}&${queryString}`)
      .then(({ data }) => {
        setTableData({
          count: data.total,
          menus: data.menus,
        });
      })
      .catch(({ response }) => {
        console.error(response);
        setTableData({ count: 0, menus: [] });
      });
  };

  return (
    <Fragment>
      <Breadcrumb icon={<MenuBook />} title="Manage" parent="Menus" />

      <div className="container-fluid">
        <FilterAccordian filters={getFilters()}>
          <form className="theme-form" noValidate="" onSubmit={onSearch}>
            <div className="form-group row mx-0">
              <FormControl>
                <InputLabel id="demo-simple-select-helper-label">Menu</InputLabel>
                <Select
                  name="display_name"
                  value={searchInput.display_name}
                  onChange={(e) => onChangeInput(e, e.target.value, 'display_name')}
                  labelId="demo-simple-select-helper-label"
                  id="demo-simple-select-helper"
                  label="Menu"
                  style={{
                    fontStyle: searchInput.display_name === 'All' ? 'italic' : 'normal',
                  }}
                >
                  <MenuItem value={'All'} style={{ fontStyle: 'italic' }}>
                    All
                  </MenuItem>
                  {menuNames.menus.map((m) => (
                    <MenuItem key={m.id} value={m.display_name}>
                      {m.display_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            <div className="form-group row mx-0">
              <FormControl>
                <InputLabel id="demo-simple-select-helper-label">Schools</InputLabel>
                <Select
                  name="schools"
                  value={searchInput.schools}
                  onChange={(e) => onChangeInput(e, e.target.value, 'schools')}
                  labelId="demo-simple-select-helper-label"
                  id="demo-simple-select-helper"
                  label="Schools"
                  style={{
                    fontStyle: searchInput.schools === 'All' ? 'italic' : 'normal',
                  }}
                >
                  <MenuItem value={'All'} style={{ fontStyle: 'italic' }}>
                    All
                  </MenuItem>
                  {schoolsNames.schools.map((i, index) => (
                    <MenuItem key={index.toString()} value={i.display_name}>
                      {i.display_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            <div className="form-group row mx-0">
              <FormControl>
                <InputLabel id="demo-simple-select-helper-label">Menu SKU Types</InputLabel>
                <Select
                  name="sku"
                  value={searchInput.sku}
                  onChange={(e) => onChangeInput(e, e.target.value, 'sku')}
                  labelId="demo-simple-select-helper-label"
                  id="demo-simple-select-helper"
                  label="Menu SKU Types"
                  style={{
                    fontStyle: searchInput.sku === 'All' ? 'italic' : 'normal',
                  }}
                >
                  <MenuItem value={'All'} style={{ fontStyle: 'italic' }}>
                    All
                  </MenuItem>
                  {filterOptions.skuTypes.map((m) => (
                    <MenuItem key={m.id} value={m.id}>
                      {m.display_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>

            <div className="form-group row mx-0">
              <FormControl>
                <InputLabel id="demo-simple-select-helper-label">Status</InputLabel>
                <Select
                  name="is_active"
                  value={searchInput.is_active}
                  onChange={(e) => onChangeInput(e, e.target.value, 'is_active')}
                  labelId="demo-simgple-select-helper-label"
                  id="demo-simple-select-helper"
                  label="Status"
                  style={{
                    fontStyle: searchInput.is_active === 'All' ? 'italic' : 'normal',
                  }}
                >
                  <MenuItem value={'All'} style={{ fontStyle: 'italic' }}>
                    All
                  </MenuItem>
                  <MenuItem value="ACTIVE">Active</MenuItem>
                  <MenuItem value="INACTIVE">Inactive</MenuItem>
                </Select>
              </FormControl>
            </div>
          </form>
        </FilterAccordian>
      </div>
      <Alert className="m-15 d-flex gap-3" color="secondary dark">
        <AlertCircle />
        <p>Each day represents the number of SKU Types for the specific Menu</p>
      </Alert>
      <button
        onClick={() => {
          setModalState({ open: true, update: false });
          reset(defaultValues);
          // clearErrors();
        }}
        className="btn btn-secondary m-15"
        type="submit"
      >
        <i className="fa fa-plus fa-xl" aria-hidden="true"></i>
      </button>
      <Modal
        isOpen={modalState.open}
        toggle={() => {
          setModalState({ open: false, update: false });
          reset(defaultValues, {});
        }}
        size="lg"
        centered={true}
      >
        <ModalHeader
          toggle={() => {
            setModalState({ open: false, update: false });
          }}
        >
          {modalState.update ? 'Edit' : 'Create'} Menu Details
        </ModalHeader>
        <ModalBody>
          <div className="card-body">
            <form className="theme-form" onSubmit={handleSubmit(onSubmit)}>
              <div className="form-group row">
                <TextField
                  label="Name"
                  variant="outlined"
                  {...register(`display_name`)}
                  error={!!(errors.display_name?.message && isSubmitted && getValues().display_name === '')}
                />
              </div>

              {modalState.update && (
                <div className="d-flex justify-content-start my-4">
                  <FormGroup>
                    <FormControlLabel
                      control={<Switch {...register(`is_active`)} checked={watch('is_active')} />}
                      label="Active Status"
                      sx={{ margin: 0 }}
                      labelPlacement="start"
                    />
                  </FormGroup>
                </div>
              )}
              <button className="btn btn-primary" type="submit">
                {modalState.update ? 'Save' : 'Create'}
              </button>
            </form>
          </div>
        </ModalBody>
      </Modal>
      <div className="container-fluid">
        <CustomTable
          rows={tableData.menus}
          actions={actionMethods}
          columns={columns}
          setPageSize={setPerPage}
          setPage={setPage}
          rowCount={tableData.count}
          pageSize={perPage}
          loading={dataIsFetching}
        />
      </div>
    </Fragment>
  );
};

const defaultValues = {
  display_name: '',
  display_name_menu: '',
  is_active: true,
};

const validationSchema = Yup.object().shape({
  display_name: Yup.string().required('Required'),
  is_active: Yup.boolean().required(),
});

export default Manage;
